/**
 * CoachJournalPanel.jsx — iter826 (Feb 2026)
 *
 * Longitudinal history for the AI Nutrition Coach. Shows:
 *   • A compliance-over-time line chart from /journal/timeseries
 *   • A stale-since notification badge when >=7 days elapsed
 *   • A collapsible list of past reports (headline + trend + top gaps
 *     + list of micro-corrections + recipe IDs offered at the time)
 *
 * Positioned inside the BHI page directly under NutritionCoachPanel so
 * the user sees "How my compliance has moved since AI suggestions".
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  History, ChevronDown, ChevronRight, AlertCircle, Trash2, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/App";
import { useHealthEvent } from "@/utils/healthEvents";

export default function CoachJournalPanel({ isDark = false }) {
  const { API, user } = useAuth();
  const [items, setItems]       = useState(null);
  const [series, setSeries]     = useState([]);
  const [stale, setStale]       = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [{ data: list }, { data: ts }] = await Promise.all([
        axios.get(`${API}/bhi/nutrition-coach/journal?limit=12`),
        axios.get(`${API}/bhi/nutrition-coach/journal/timeseries?limit=12`),
      ]);
      setItems(list?.items || []);
      setStale({ days: list?.stale_since_days, should: list?.should_regenerate });
      setSeries(ts?.points || []);
    } catch {
      setItems([]);
    }
  }, [API, user]);

  useEffect(() => { load(); }, [load]);
  useHealthEvent("coach-journal-changed", () => { load(); });

  const remove = useCallback(async (id) => {
    if (!window.confirm("Изтриване на записа?")) return;
    try {
      await axios.delete(`${API}/bhi/nutrition-coach/journal/${id}`);
      setItems((it) => (it || []).filter((x) => x.journal_id !== id));
    } catch { /* noop */ }
  }, [API]);

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"
  }`;

  if (!user) return null;
  if (items === null) return null;
  if (items.length === 0) return null;  // hide entirely until first generation

  return (
    <section
      className={`${cardCls} p-5 mb-4`}
      data-testid="coach-journal-panel"
    >
      <header className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center shrink-0">
          <History className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-extrabold bg-gradient-to-r from-violet-500 to-indigo-500 bg-clip-text text-transparent">
            📅 Coach Journal — история
          </h3>
          <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Как се движи Вашият compliance във времето след всяка AI препоръка. Всеки запис включва заглавие, посока, най-важните пропуски и препоръчаните рецепти.
          </p>
        </div>
      </header>

      {stale?.should && (
        <div
          className="mb-4 p-3 rounded-xl flex items-start gap-2 bg-amber-500/10 text-amber-700 dark:text-amber-300"
          data-testid="coach-journal-stale-badge"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="text-[12px]">
            <b>Изминаха {stale.days} дни от последната препоръка.</b> Време е за нов AI анализ — виж горе.
          </div>
        </div>
      )}

      {/* Compliance trend chart */}
      {series.length >= 2 && (
        <ComplianceChart series={series} isDark={isDark} />
      )}

      {/* Journal list */}
      <div className="space-y-2 mt-3" data-testid="coach-journal-list">
        {items.map((it) => (
          <JournalRow
            key={it.journal_id}
            it={it}
            isOpen={expanded === it.journal_id}
            onToggle={() => setExpanded(expanded === it.journal_id ? null : it.journal_id)}
            onDelete={() => remove(it.journal_id)}
            isDark={isDark}
          />
        ))}
      </div>
    </section>
  );
}

// ─────────── Sub-components ───────────

function ComplianceChart({ series, isDark }) {
  const chartMeta = useMemo(() => {
    if (!series.length) return null;
    const maxN = Math.max(...series.map((p) => p.nis || 0), 1);
    const maxD = Math.max(...series.map((p) => p.dil || 0), 1);
    return { maxN, maxD };
  }, [series]);
  if (!chartMeta) return null;

  const H = 90;
  const W = 100; // percent width via viewBox
  const stepX = W / Math.max(series.length - 1, 1);

  const linePath = (accessor, scale) => {
    return series
      .map((p, i) => {
        const y = H - (accessor(p) / scale) * H;
        const x = i * stepX;
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${isFinite(y) ? y.toFixed(1) : H}`;
      })
      .join(" ");
  };

  return (
    <div
      className={`p-3 rounded-xl mt-2 ${isDark ? "bg-slate-900/60 border border-slate-700" : "bg-slate-50 border border-slate-200"}`}
      data-testid="coach-journal-chart"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase font-bold tracking-widest opacity-60 flex items-center gap-1">
          <TrendingUp className="w-3 h-3" />
          Посока през последните {series.length} анализа
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Compliance</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500"></span> NIS</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> DIL</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-24">
        <path d={linePath((p) => p.compliance_pct, 100)} stroke="rgb(16,185,129)" strokeWidth="1.2" fill="none" />
        <path d={linePath((p) => p.nis || 0, chartMeta.maxN * 1.2)} stroke="rgb(244,63,94)"  strokeWidth="1.2" fill="none" strokeDasharray="2 2" />
        <path d={linePath((p) => p.dil || 0, chartMeta.maxD * 1.2)} stroke="rgb(245,158,11)" strokeWidth="1.2" fill="none" strokeDasharray="2 2" />
        {series.map((p, i) => {
          const x = i * stepX;
          const y = H - (p.compliance_pct / 100) * H;
          return <circle key={i} cx={x} cy={isFinite(y) ? y : H} r="0.9" fill="rgb(16,185,129)" />;
        })}
      </svg>
      <div className="flex justify-between mt-1 text-[9px] opacity-50">
        {series.map((p, i) => (
          <span key={i}>{p.date ? new Date(p.date).toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit" }) : ""}</span>
        ))}
      </div>
    </div>
  );
}

function JournalRow({ it, isOpen, onToggle, onDelete, isDark }) {
  const date = it.created_at ? new Date(it.created_at).toLocaleDateString("bg-BG", {
    year: "numeric", month: "short", day: "numeric",
  }) : "";
  const rowCls = `rounded-lg border ${isDark ? "border-slate-700 bg-slate-900/40" : "border-slate-200 bg-white"}`;
  return (
    <div className={rowCls} data-testid={`coach-journal-entry-${it.journal_id}`}>
      <div className="flex items-center gap-2 p-2.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          data-testid={`coach-journal-toggle-${it.journal_id}`}
        >
          {isOpen ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{it.headline || "—"}</div>
            <div className="text-[10px] opacity-60">
              {date} · {it.ai_generated ? "AI" : "детерминистично"} · {it.weekly?.length || 0} седмици
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Изтрий"
          className="p-1.5 rounded hover:bg-rose-100 dark:hover:bg-rose-500/20 text-rose-500"
          data-testid={`coach-journal-delete-${it.journal_id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
          {it.trend_analysis && (
            <div className={`p-2.5 rounded-lg text-[11px] ${isDark ? "bg-indigo-500/10" : "bg-indigo-50"}`}>
              {it.trend_analysis}
            </div>
          )}
          {(it.top_missed_foods || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-60">Пропускани храни</div>
              <div className="flex flex-wrap gap-1">
                {it.top_missed_foods.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300">
                    {m.item} · {m.missed_count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(it.micro_corrections || []).length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-60">Препоръчани корекции</div>
              <div className="space-y-1">
                {it.micro_corrections.map((c, i) => (
                  <div key={i} className={`p-2 rounded ${isDark ? "bg-slate-800/50" : "bg-slate-50"}`}>
                    <div className="text-[11px] font-semibold">{c.title}</div>
                    <div className="text-[10px] opacity-70 flex flex-wrap gap-1 mt-0.5">
                      {c.target_pathway && (
                        <span className="inline-flex px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300">
                          🧬 {c.target_pathway}
                        </span>
                      )}
                      {c.food_hint && (
                        <span className="inline-flex px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300">
                          🥗 {c.food_hint}
                        </span>
                      )}
                      {(c.recipes_offered || []).length > 0 && (
                        <span className="inline-flex px-1.5 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300">
                          👨‍🍳 {c.recipes_offered.length} рецепти
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {it.closing && (
            <div className="text-[11px] italic opacity-70">{it.closing}</div>
          )}
        </div>
      )}
    </div>
  );
}
